// Report Chart Service - Pure SVG chart generators for HTML export
// All functions return self-contained SVG strings (no external dependencies)

const reportChartService = {
  // Color palettes
  colors: {
    primary: ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#16a34a', '#0891b2', '#4f46e5', '#c026d3'],
    severity: { critical: '#dc2626', high: '#ea580c', medium: '#f59e0b', low: '#22c55e' },
    risk: { immediate: '#dc2626', high: '#ea580c', medium: '#f59e0b', low: '#22c55e' },
    grade: { A: '#22c55e', B: '#3b82f6', C: '#f59e0b', D: '#ea580c', F: '#dc2626' },
    tactics: {
      'Reconnaissance': '#6366f1', 'Resource Development': '#8b5cf6', 'Initial Access': '#a855f7',
      'Execution': '#d946ef', 'Persistence': '#ec4899', 'Privilege Escalation': '#f43f5e',
      'Defense Evasion': '#ef4444', 'Credential Access': '#f97316', 'Discovery': '#f59e0b',
      'Lateral Movement': '#eab308', 'Collection': '#84cc16', 'Command and Control': '#22c55e',
      'Exfiltration': '#14b8a6', 'Impact': '#06b6d4'
    }
  },

  // Generate a donut chart SVG
  generateDonutChart(segments, options = {}) {
    const { width = 300, height = 300, innerRadius = 60, outerRadius = 100, title = '' } = options;
    const cx = width / 2;
    const cy = height / 2;
    const total = segments.reduce((sum, s) => sum + (s.value || 0), 0);

    if (total === 0) return `<svg width="${width}" height="${height}"><text x="${cx}" y="${cy}" text-anchor="middle" fill="#6b7280" font-size="14">No data</text></svg>`;

    let currentAngle = -Math.PI / 2;
    const paths = [];
    const labels = [];

    segments.forEach((seg, i) => {
      if (seg.value <= 0) return;
      const sliceAngle = (seg.value / total) * 2 * Math.PI;
      const endAngle = currentAngle + sliceAngle;
      const largeArc = sliceAngle > Math.PI ? 1 : 0;

      const x1 = cx + outerRadius * Math.cos(currentAngle);
      const y1 = cy + outerRadius * Math.sin(currentAngle);
      const x2 = cx + outerRadius * Math.cos(endAngle);
      const y2 = cy + outerRadius * Math.sin(endAngle);
      const x3 = cx + innerRadius * Math.cos(endAngle);
      const y3 = cy + innerRadius * Math.sin(endAngle);
      const x4 = cx + innerRadius * Math.cos(currentAngle);
      const y4 = cy + innerRadius * Math.sin(currentAngle);

      const color = seg.color || this.colors.primary[i % this.colors.primary.length];
      paths.push(`<path d="M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4} Z" fill="${color}" stroke="white" stroke-width="2"/>`);

      // Label
      const midAngle = currentAngle + sliceAngle / 2;
      const labelR = outerRadius + 20;
      const lx = cx + labelR * Math.cos(midAngle);
      const ly = cy + labelR * Math.sin(midAngle);
      const pct = Math.round((seg.value / total) * 100);
      if (pct >= 5) {
        labels.push(`<text x="${lx}" y="${ly}" text-anchor="${lx > cx ? 'start' : 'end'}" fill="#374151" font-size="11">${seg.label}: ${pct}%</text>`);
      }

      currentAngle = endAngle;
    });

    // Center total
    const centerText = `<text x="${cx}" y="${cy - 8}" text-anchor="middle" fill="#1f2937" font-size="24" font-weight="bold">${total}</text>
      <text x="${cx}" y="${cy + 12}" text-anchor="middle" fill="#6b7280" font-size="11">Total</text>`;

    const titleSvg = title ? `<text x="${cx}" y="20" text-anchor="middle" fill="#1f2937" font-size="14" font-weight="600">${title}</text>` : '';

    return `<svg width="${width}" height="${height + 40}" viewBox="0 0 ${width} ${height + 40}" xmlns="http://www.w3.org/2000/svg">
      ${titleSvg}
      <g transform="translate(0, ${title ? 30 : 0})">
        ${paths.join('\n')}
        ${centerText}
        ${labels.join('\n')}
      </g>
    </svg>`;
  },

  // Generate a bar chart SVG
  generateBarChart(data, options = {}) {
    const { width = 500, height = 300, title = '', barColor = '#2563eb', showValues = true } = options;
    const margin = { top: title ? 40 : 20, right: 20, bottom: 60, left: 50 };
    const chartW = width - margin.left - margin.right;
    const chartH = height - margin.top - margin.bottom;

    if (!data || data.length === 0) return `<svg width="${width}" height="${height}"><text x="${width/2}" y="${height/2}" text-anchor="middle" fill="#6b7280" font-size="14">No data</text></svg>`;

    const maxVal = Math.max(...data.map(d => d.value || 0), 1);
    const barWidth = Math.min(40, (chartW / data.length) * 0.7);
    const barGap = (chartW - barWidth * data.length) / (data.length + 1);

    const bars = data.map((d, i) => {
      const barH = ((d.value || 0) / maxVal) * chartH;
      const x = margin.left + barGap + i * (barWidth + barGap);
      const y = margin.top + chartH - barH;
      const color = d.color || barColor;

      let valueText = '';
      if (showValues) {
        valueText = `<text x="${x + barWidth / 2}" y="${y - 5}" text-anchor="middle" fill="#374151" font-size="11" font-weight="600">${d.value}</text>`;
      }

      const labelText = `<text x="${x + barWidth / 2}" y="${margin.top + chartH + 20}" text-anchor="middle" fill="#6b7280" font-size="10" transform="rotate(-30, ${x + barWidth / 2}, ${margin.top + chartH + 20})">${d.label || ''}</text>`;

      return `<rect x="${x}" y="${y}" width="${barWidth}" height="${barH}" fill="${color}" rx="2"/>
        ${valueText}
        ${labelText}`;
    }).join('\n');

    // Grid lines
    const gridLines = [0, 0.25, 0.5, 0.75, 1].map(pct => {
      const y = margin.top + chartH * (1 - pct);
      const val = Math.round(maxVal * pct);
      return `<line x1="${margin.left}" y1="${y}" x2="${margin.left + chartW}" y2="${y}" stroke="#e5e7eb" stroke-width="1"/>
        <text x="${margin.left - 5}" y="${y + 4}" text-anchor="end" fill="#9ca3af" font-size="10">${val}</text>`;
    }).join('\n');

    const titleSvg = title ? `<text x="${width / 2}" y="20" text-anchor="middle" fill="#1f2937" font-size="14" font-weight="600">${title}</text>` : '';

    return `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      ${titleSvg}
      ${gridLines}
      <line x1="${margin.left}" y1="${margin.top}" x2="${margin.left}" y2="${margin.top + chartH}" stroke="#d1d5db" stroke-width="1"/>
      <line x1="${margin.left}" y1="${margin.top + chartH}" x2="${margin.left + chartW}" y2="${margin.top + chartH}" stroke="#d1d5db" stroke-width="1"/>
      ${bars}
    </svg>`;
  },

  // Generate a gauge chart SVG (A-F grade)
  generateGaugeChart(score, maxScore, options = {}) {
    const { width = 200, height = 140, title = '' } = options;
    const cx = width / 2;
    const cy = height - 20;
    const radius = 70;
    const pct = Math.min(score / maxScore, 1);

    // Arc from 180deg to 0deg (left to right)
    const startAngle = Math.PI;
    const endAngle = Math.PI * (1 - pct);
    const x1 = cx + radius * Math.cos(startAngle);
    const y1 = cy + radius * Math.sin(startAngle);
    const x2 = cx + radius * Math.cos(endAngle);
    const y2 = cy + radius * Math.sin(endAngle);
    const largeArc = pct > 0.5 ? 1 : 0;

    // Color based on percentage
    const color = pct >= 0.8 ? '#22c55e' : pct >= 0.6 ? '#3b82f6' : pct >= 0.4 ? '#f59e0b' : pct >= 0.2 ? '#ea580c' : '#dc2626';

    const titleSvg = title ? `<text x="${cx}" y="16" text-anchor="middle" fill="#1f2937" font-size="12" font-weight="600">${title}</text>` : '';

    return `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      ${titleSvg}
      <path d="M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}" fill="none" stroke="#e5e7eb" stroke-width="14" stroke-linecap="round"/>
      <path d="M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}" fill="none" stroke="${color}" stroke-width="14" stroke-linecap="round"/>
      <text x="${cx}" y="${cy - 10}" text-anchor="middle" fill="${color}" font-size="28" font-weight="bold">${Math.round(pct * 100)}%</text>
    </svg>`;
  },

  // Generate a horizontal bar chart SVG
  generateHorizontalBarChart(items, options = {}) {
    const { width = 500, height: optHeight, title = '', maxLabelWidth = 150 } = options;
    const barHeight = 28;
    const barGap = 8;
    const height = optHeight || (items.length * (barHeight + barGap) + 60);
    const margin = { top: title ? 40 : 20, right: 60, left: maxLabelWidth + 10 };
    const chartW = width - margin.left - margin.right;

    if (!items || items.length === 0) return `<svg width="${width}" height="60"><text x="${width/2}" y="30" text-anchor="middle" fill="#6b7280" font-size="14">No data</text></svg>`;

    const maxVal = Math.max(...items.map(d => d.value || 0), 1);

    const bars = items.map((item, i) => {
      const y = margin.top + i * (barHeight + barGap);
      const barW = ((item.value || 0) / maxVal) * chartW;
      const color = item.color || this.colors.primary[i % this.colors.primary.length];

      return `<text x="${margin.left - 8}" y="${y + barHeight / 2 + 4}" text-anchor="end" fill="#374151" font-size="11">${(item.label || '').substring(0, 25)}</text>
        <rect x="${margin.left}" y="${y}" width="${barW}" height="${barHeight}" fill="${color}" rx="3"/>
        <text x="${margin.left + barW + 6}" y="${y + barHeight / 2 + 4}" fill="#374151" font-size="11" font-weight="600">${item.value}</text>`;
    }).join('\n');

    const titleSvg = title ? `<text x="${width / 2}" y="20" text-anchor="middle" fill="#1f2937" font-size="14" font-weight="600">${title}</text>` : '';

    return `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      ${titleSvg}
      ${bars}
    </svg>`;
  },

  // Generate MITRE ATT&CK Navigator-style heatmap grid
  generateMitreHeatmap(techniques, options = {}) {
    const { width = 1400, title = 'MITRE ATT&CK Coverage', theme = 'light' } = options;
    const isDark = theme === 'dark';

    // Theme colors
    const colors = isDark ? {
      bg: '#1a1a2e', titleFill: '#f1f5f9', headerFill: '#e2e8f0',
      guideLine: '#334155', emptyFill: '#16213e', emptyStroke: '#334155',
      emptyText: '#64748b', legendText: '#cbd5e1', cellTextLight: '#f1f5f9', cellTextDark: '#e2e8f0'
    } : {
      bg: '#fafafa', titleFill: '#1f2937', headerFill: '#374151',
      guideLine: '#d1d5db', emptyFill: '#f3f4f6', emptyStroke: '#e5e7eb',
      emptyText: '#9ca3af', legendText: '#374151', cellTextLight: '#ffffff', cellTextDark: '#1f2937'
    };

    const tactics = [
      'Reconnaissance', 'Resource Development', 'Initial Access', 'Execution',
      'Persistence', 'Privilege Escalation', 'Defense Evasion', 'Credential Access',
      'Discovery', 'Lateral Movement', 'Collection', 'Command and Control',
      'Exfiltration', 'Impact'
    ];

    // Group techniques by tactic
    const byTactic = {};
    tactics.forEach(t => { byTactic[t] = []; });

    if (techniques && techniques.length > 0) {
      techniques.forEach(tech => {
        const tactic = tech.tactic || 'Unknown';
        if (byTactic[tactic]) {
          byTactic[tactic].push(tech);
        }
      });
    }

    const colWidth = Math.floor((width - 20) / tactics.length);
    const cellH = 38;
    const headerH = 90;
    const maxTechsInCol = Math.max(...Object.values(byTactic).map(arr => arr.length), 1);
    const height = headerH + maxTechsInCol * (cellH + 2) + 80;

    // Header - tactic names with mild abbreviation, horizontal
    const tacticAbbrev = {
      'Reconnaissance': 'Reconnaissance',
      'Resource Development': 'Resource Dev.',
      'Initial Access': 'Initial Access',
      'Execution': 'Execution',
      'Persistence': 'Persistence',
      'Privilege Escalation': 'Privilege Esc.',
      'Defense Evasion': 'Defense Evasion',
      'Credential Access': 'Credential Access',
      'Discovery': 'Discovery',
      'Lateral Movement': 'Lateral Movement',
      'Collection': 'Collection',
      'Command and Control': 'Command & Control',
      'Exfiltration': 'Exfiltration',
      'Impact': 'Impact'
    };
    const headers = tactics.map((tactic, i) => {
      const x = 10 + i * colWidth;
      const abbrev = tacticAbbrev[tactic] || tactic;
      return `<text x="${x + colWidth / 2}" y="50" text-anchor="middle" fill="${colors.headerFill}" font-size="9" font-weight="600">${abbrev}</text>
        <line x1="${x + colWidth / 2}" y1="56" x2="${x + colWidth / 2}" y2="${headerH - 2}" stroke="${colors.guideLine}" stroke-width="1" stroke-dasharray="2"/>`;
    }).join('\n');

    // Cells
    const cells = [];
    tactics.forEach((tactic, colIdx) => {
      const x = 10 + colIdx * colWidth;
      const techs = byTactic[tactic];

      techs.forEach((tech, rowIdx) => {
        const y = headerH + rowIdx * (cellH + 2);
        const successRate = tech.successRate || 0;
        const r = Math.round(220 * successRate + 34 * (1 - successRate));
        const g = Math.round(38 * successRate + 197 * (1 - successRate));
        const b = Math.round(38 * successRate + 94 * (1 - successRate));
        const color = `rgb(${r},${g},${b})`;
        const textColor = successRate > 0.5 ? colors.cellTextLight : colors.cellTextDark;
        const techId = tech.technique || tech.id || '';
        const techName = tech.name || '';
        const truncName = techName.length > 14 ? techName.substring(0, 14) + '..' : techName;
        const count = tech.attempts || tech.count || 0;

        cells.push(`<rect x="${x + 1}" y="${y}" width="${colWidth - 2}" height="${cellH}" fill="${color}" rx="2" opacity="0.9"/>
          <text x="${x + colWidth / 2}" y="${y + 13}" text-anchor="middle" fill="${textColor}" font-size="8" font-weight="600">${techId}</text>
          <text x="${x + colWidth / 2}" y="${y + 24}" text-anchor="middle" fill="${textColor}" font-size="7.5">${truncName}</text>
          <text x="${x + colWidth / 2}" y="${y + 34}" text-anchor="middle" fill="${textColor}" font-size="7">${count}x</text>`);
      });

      // Empty column placeholder
      if (techs.length === 0) {
        cells.push(`<rect x="${x + 1}" y="${headerH}" width="${colWidth - 2}" height="${cellH}" fill="${colors.emptyFill}" rx="2" stroke="${colors.emptyStroke}" stroke-dasharray="4"/>
          <text x="${x + colWidth / 2}" y="${headerH + 20}" text-anchor="middle" fill="${colors.emptyText}" font-size="9">-</text>`);
      }
    });

    // Legend
    const legendY = height - 40;
    const legend = `<rect x="10" y="${legendY}" width="20" height="12" fill="#22c55e" rx="2"/>
      <text x="34" y="${legendY + 10}" fill="${colors.legendText}" font-size="10">Blocked/Detected</text>
      <rect x="180" y="${legendY}" width="20" height="12" fill="#f59e0b" rx="2"/>
      <text x="204" y="${legendY + 10}" fill="${colors.legendText}" font-size="10">Partial</text>
      <rect x="300" y="${legendY}" width="20" height="12" fill="#dc2626" rx="2"/>
      <text x="324" y="${legendY + 10}" fill="${colors.legendText}" font-size="10">Successful (Gap)</text>`;

    const titleSvg = `<text x="${width / 2}" y="22" text-anchor="middle" fill="${colors.titleFill}" font-size="14" font-weight="600">${title}</text>`;

    return `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
      <rect width="${width}" height="${height}" fill="${colors.bg}" rx="8"/>
      ${titleSvg}
      ${headers}
      ${cells.join('\n')}
      ${legend}
    </svg>`;
  },

  // Generate Security Posture Grade gauge (A-F with colored arc)
  generateSecurityPostureGauge(grade, score, options = {}) {
    const { width = 260, height = 200 } = options;
    const cx = width / 2;
    const cy = 140;
    const radius = 80;

    const gradeColors = { A: '#22c55e', B: '#3b82f6', C: '#f59e0b', D: '#ea580c', F: '#dc2626' };
    const color = gradeColors[grade] || '#6b7280';
    const pct = Math.min(Math.max(score / 100, 0), 1);

    // Background arc
    const bgArc = this._describeArc(cx, cy, radius, 180, 360);
    // Filled arc
    const fillEnd = 180 + pct * 180;
    const fillArc = this._describeArc(cx, cy, radius, 180, fillEnd);

    // Grade segments markers
    const markers = [
      { angle: 180, label: 'F' },
      { angle: 216, label: 'D' },
      { angle: 252, label: 'C' },
      { angle: 288, label: 'B' },
      { angle: 324, label: 'A' },
      { angle: 360, label: '' }
    ];

    const markerLines = markers.map(m => {
      const rad = (m.angle * Math.PI) / 180;
      const x1 = cx + (radius - 10) * Math.cos(rad);
      const y1 = cy + (radius - 10) * Math.sin(rad);
      const x2 = cx + (radius + 10) * Math.cos(rad);
      const y2 = cy + (radius + 10) * Math.sin(rad);
      const lx = cx + (radius + 22) * Math.cos(rad);
      const ly = cy + (radius + 22) * Math.sin(rad);
      return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#d1d5db" stroke-width="1.5"/>
        ${m.label ? `<text x="${lx}" y="${ly + 3}" text-anchor="middle" fill="#9ca3af" font-size="10">${m.label}</text>` : ''}`;
    }).join('\n');

    return `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
      <text x="${cx}" y="20" text-anchor="middle" fill="#1f2937" font-size="14" font-weight="600">Security Posture</text>
      <path d="${bgArc}" fill="none" stroke="#e5e7eb" stroke-width="18" stroke-linecap="round"/>
      <path d="${fillArc}" fill="none" stroke="${color}" stroke-width="18" stroke-linecap="round"/>
      ${markerLines}
      <text x="${cx}" y="${cy - 15}" text-anchor="middle" fill="${color}" font-size="48" font-weight="bold">${grade}</text>
      <text x="${cx}" y="${cy + 8}" text-anchor="middle" fill="#6b7280" font-size="14">${score}/100</text>
    </svg>`;
  },

  // Helper: describe SVG arc path
  _describeArc(cx, cy, radius, startAngle, endAngle) {
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const x1 = cx + radius * Math.cos(startRad);
    const y1 = cy + radius * Math.sin(startRad);
    const x2 = cx + radius * Math.cos(endRad);
    const y2 = cy + radius * Math.sin(endRad);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
  },

  // Helper: escape XML special characters for SVG text content
  _escapeXml(str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  },

  // Generate WSTG coverage heatmap SVG for reports
  generateWstgCoverageChart(coverage, options = {}) {
    const { width = 800, height = 200, title = 'WSTG Coverage' } = options;
    const statusColors = { pass: '#22c55e', fail: '#dc2626', info: '#3b82f6', not_tested: '#d1d5db' };

    if (!coverage || coverage.length === 0) {
      return `<svg width="${width}" height="60"><text x="${width/2}" y="30" text-anchor="middle" fill="#6b7280" font-size="14">No WSTG data</text></svg>`;
    }

    // Group by category
    const grouped = {};
    coverage.forEach(t => {
      if (!grouped[t.categoryCode]) grouped[t.categoryCode] = [];
      grouped[t.categoryCode].push(t);
    });

    const cats = Object.keys(grouped).sort();
    const colW = Math.floor((width - 20) / cats.length);
    const cellH = 8;
    const headerH = 40;
    const maxTests = Math.max(...Object.values(grouped).map(a => a.length), 1);
    const totalH = headerH + maxTests * (cellH + 1) + 40;

    const headers = cats.map((cat, i) => {
      const x = 10 + i * colW;
      return `<text x="${x + colW/2}" y="${headerH - 8}" text-anchor="middle" fill="#374151" font-size="9" font-weight="600">${cat.replace('WSTG-','')}</text>`;
    }).join('\n');

    const cells = [];
    cats.forEach((cat, ci) => {
      const x = 10 + ci * colW;
      grouped[cat].forEach((test, ri) => {
        const y = headerH + ri * (cellH + 1);
        const color = statusColors[test.status] || statusColors.not_tested;
        cells.push(`<rect x="${x + 1}" y="${y}" width="${colW - 2}" height="${cellH}" fill="${color}" rx="1"/>`);
      });
    });

    const titleSvg = title ? `<text x="${width/2}" y="16" text-anchor="middle" fill="#1f2937" font-size="13" font-weight="600">${title}</text>` : '';

    const legendY = totalH - 20;
    const legend = `<rect x="10" y="${legendY}" width="12" height="8" fill="#22c55e" rx="1"/><text x="26" y="${legendY+7}" fill="#374151" font-size="9">Pass</text>
      <rect x="80" y="${legendY}" width="12" height="8" fill="#dc2626" rx="1"/><text x="96" y="${legendY+7}" fill="#374151" font-size="9">Fail</text>
      <rect x="140" y="${legendY}" width="12" height="8" fill="#3b82f6" rx="1"/><text x="156" y="${legendY+7}" fill="#374151" font-size="9">Info</text>
      <rect x="200" y="${legendY}" width="12" height="8" fill="#d1d5db" rx="1"/><text x="216" y="${legendY+7}" fill="#374151" font-size="9">Untested</text>`;

    return `<svg width="${width}" height="${totalH}" viewBox="0 0 ${width} ${totalH}" xmlns="http://www.w3.org/2000/svg" style="font-family: -apple-system, BlinkMacSystemFont, sans-serif;">
      ${titleSvg}
      ${headers}
      ${cells.join('\n')}
      ${legend}
    </svg>`;
  },

  // Generate WAF effectiveness bar chart SVG
  generateWafChart(wafData, options = {}) {
    const { width = 500, height = 300, title = 'WAF Effectiveness by Attack Category' } = options;

    if (!wafData || !wafData.categories || wafData.categories.length === 0) {
      return `<svg width="${width}" height="60"><text x="${width/2}" y="30" text-anchor="middle" fill="#6b7280" font-size="14">No WAF data</text></svg>`;
    }

    const data = wafData.categories.map(c => ({
      label: c.category,
      value: c.blockRate,
      color: c.blockRate >= 80 ? '#22c55e' : c.blockRate >= 50 ? '#f59e0b' : '#dc2626'
    }));

    return this.generateBarChart(data, { width, height, title, showValues: true });
  },

  // Generate Authorization Matrix SVG (endpoints × auth profiles grid)
  generateAuthzMatrixSVG(authzData, options = {}) {
    const { width = 700, title = 'Authorization Matrix' } = options;

    if (!authzData || !authzData.endpoints || authzData.endpoints.length === 0 ||
        !authzData.profiles || authzData.profiles.length === 0) {
      return `<svg width="${width}" height="60"><text x="${width/2}" y="30" text-anchor="middle" fill="#6b7280" font-size="14">No authorization data</text></svg>`;
    }

    const { endpoints, profiles, results } = authzData;
    const cellW = 80;
    const cellH = 28;
    const labelW = 200;
    const headerH = 60;
    const titleH = title ? 30 : 0;
    const height = titleH + headerH + endpoints.length * (cellH + 2) + 40;
    const statusColors = { enforced: '#22c55e', bypassed: '#dc2626', partial: '#f59e0b', untested: '#d1d5db' };

    // Build result lookup
    const resultMap = new Map();
    (results || []).forEach(r => {
      resultMap.set(`${r.endpointIdx}-${r.profileIdx}`, r.status);
    });

    const titleSvg = title ? `<text x="${width/2}" y="20" text-anchor="middle" fill="#1f2937" font-size="13" font-weight="600">${title}</text>` : '';

    // Profile headers
    const headers = profiles.map((p, i) => {
      const x = labelW + i * (cellW + 2) + cellW / 2;
      return `<text x="${x}" y="${titleH + headerH - 10}" text-anchor="middle" fill="${p.color || '#374151'}" font-size="10" font-weight="600">${p.name}</text>`;
    }).join('\n');

    // Rows
    const rows = endpoints.map((ep, ei) => {
      const y = titleH + headerH + ei * (cellH + 2);
      const label = `<text x="${labelW - 8}" y="${y + cellH / 2 + 4}" text-anchor="end" fill="#374151" font-size="10">${(ep.method || 'GET')} ${(ep.path || '').substring(0, 25)}</text>`;
      const cells = profiles.map((_, pi) => {
        const cx = labelW + pi * (cellW + 2);
        const status = resultMap.get(`${ei}-${pi}`) || 'untested';
        const color = statusColors[status] || statusColors.untested;
        return `<rect x="${cx}" y="${y}" width="${cellW}" height="${cellH}" fill="${color}" rx="3" opacity="0.85"/>
          <text x="${cx + cellW / 2}" y="${y + cellH / 2 + 4}" text-anchor="middle" fill="${status === 'untested' ? '#6b7280' : '#fff'}" font-size="9" font-weight="500">${status}</text>`;
      }).join('\n');
      return label + '\n' + cells;
    }).join('\n');

    // Legend
    const legendY = height - 20;
    const legend = Object.entries(statusColors).map(([status, color], i) => {
      const lx = labelW + i * 100;
      return `<rect x="${lx}" y="${legendY}" width="12" height="10" fill="${color}" rx="2"/>
        <text x="${lx + 16}" y="${legendY + 8}" fill="#374151" font-size="9">${status.charAt(0).toUpperCase() + status.slice(1)}</text>`;
    }).join('\n');

    return `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" style="font-family: -apple-system, BlinkMacSystemFont, sans-serif;">
      <rect width="${width}" height="${height}" fill="#fafafa" rx="6"/>
      ${titleSvg}
      ${headers}
      ${rows}
      ${legend}
    </svg>`;
  },

  // Generate Kill Chain visualization SVG - tactic columns with individual technique nodes (matches web version)
  generateKillChainSvg(techniques, options = {}) {
    const { width = 1400, title = 'Attack Kill Chain', theme = 'light' } = options;
    const isDark = theme === 'dark';

    const c = isDark ? {
      bg: '#1f2937', bgAlt: '#111827', titleFill: '#f1f5f9',
      headerFill: '#e5e7eb', colStroke: '#374151', countBadgeBg: '#003d82',
      countBadgeStroke: '#fff', countBadgeText: '#fff',
      nameText: '#e5e7eb', legendText: '#cbd5e1', statsBg: '#374151',
      statsStroke: '#4b5563', statsText: '#e5e7eb', tooltipBg: 'rgba(0,0,0,0.92)',
      emptyText: '#4b5563'
    } : {
      bg: '#ffffff', bgAlt: '#f8f9fa', titleFill: '#1f2937',
      headerFill: '#1f2937', colStroke: '#e5e7eb', countBadgeBg: '#003d82',
      countBadgeStroke: '#fff', countBadgeText: '#fff',
      nameText: '#374151', legendText: '#374151', statsBg: '#f8f9fa',
      statsStroke: '#dee2e6', statsText: '#1f2937', tooltipBg: 'rgba(0,0,0,0.9)',
      emptyText: '#d1d5db'
    };

    const tacticOrder = [
      'Reconnaissance', 'Resource Development', 'Initial Access', 'Execution',
      'Persistence', 'Privilege Escalation', 'Defense Evasion', 'Credential Access',
      'Discovery', 'Lateral Movement', 'Collection', 'Command and Control',
      'Exfiltration', 'Impact'
    ];

    const tacticAbbrev = {
      'Reconnaissance': 'Recon', 'Resource Development': 'Resource Dev',
      'Initial Access': 'Initial Access', 'Privilege Escalation': 'Priv Esc',
      'Defense Evasion': 'Defense Evasion', 'Credential Access': 'Cred Access',
      'Lateral Movement': 'Lateral Move', 'Command and Control': 'C2',
      'Exfiltration': 'Exfil'
    };

    const tacticColors = this.colors.tactics;

    // Group techniques by tactic
    const byTactic = {};
    tacticOrder.forEach(t => { byTactic[t] = []; });
    (techniques || []).forEach(tech => {
      const tactic = tech.tactic || 'Unknown';
      if (byTactic[tactic]) {
        byTactic[tactic].push(tech);
      }
    });

    // Show ALL 14 tactic columns (including empty ones) for full kill chain coverage view
    const activeTactics = tacticOrder;
    const populatedTactics = tacticOrder.filter(t => byTactic[t].length > 0);
    if (populatedTactics.length === 0) {
      return `<svg width="${width}" height="80" viewBox="0 0 ${width} 80" xmlns="http://www.w3.org/2000/svg"><rect width="${width}" height="80" fill="${c.bg}" rx="8"/><text x="${width/2}" y="45" text-anchor="middle" fill="${c.emptyText}" font-size="14">No kill chain data available</text></svg>`;
    }

    const headerH = 50;
    const techBoxW = 80;
    const techBoxH = 32;
    const techSpacing = 55;
    const colPadding = 10;
    const maxTechsInCol = Math.max(...activeTactics.map(t => byTactic[t].length), 1);
    const colContentH = maxTechsInCol * techSpacing + 30;
    const statsH = 110;
    const legendH = 30;
    const height = headerH + colContentH + legendH + 15;
    const colW = Math.floor((width - 20) / activeTactics.length);

    const elements = [];
    const tooltips = []; // Collected separately so they render on top of everything

    // Background
    elements.push(`<rect width="${width}" height="${height}" fill="${c.bg}" rx="8"/>`);

    // Tactic columns with tactic-colored tinted backgrounds (matches web version)
    activeTactics.forEach((tactic, i) => {
      const x = 10 + i * colW;
      const tacticColor = tacticColors[tactic] || '#6366f1';

      // Column background - tactic color with low opacity overlay
      elements.push(`<rect x="${x}" y="${headerH}" width="${colW}" height="${colContentH}" fill="${isDark ? '#111827' : '#f8f9fa'}" stroke="${c.colStroke}" stroke-width="1"/>`);
      elements.push(`<rect x="${x}" y="${headerH}" width="${colW}" height="${colContentH}" fill="${tacticColor}" opacity="${isDark ? '0.12' : '0.08'}" rx="0"/>`);

      // Tactic header label in tactic color
      const label = tacticAbbrev[tactic] || tactic;
      elements.push(`<text x="${x + colW / 2}" y="${headerH - 12}" text-anchor="middle" fill="${tacticColor}" font-size="11" font-weight="bold">${label}</text>`);

      // Empty column indicator
      const techs = byTactic[tactic];
      if (techs.length === 0) {
        elements.push(`<text x="${x + colW / 2}" y="${headerH + colContentH / 2}" text-anchor="middle" fill="${c.emptyText}" font-size="10">Not Tested</text>`);
      }

      // Technique nodes within column
      techs.forEach((tech, ti) => {
        const cx = x + colW / 2;
        const cy = headerH + 30 + ti * techSpacing;
        const successRate = tech.successRate || 0;
        const pct = Math.round(successRate * 100);

        // Box color based on success rate (matches web: green ≥70%, yellow ≥30%, red <30%)
        const boxColor = pct >= 70 ? '#28a745' : pct >= 30 ? '#ffc107' : '#dc3545';

        // Unique ID for tooltip targeting
        const nodeId = `kc-${tactic.replace(/\s+/g, '')}-${ti}`;

        // Build tooltip content
        const techId = tech.technique || tech.id || '';
        const techName = tech.name || '';
        const attempts = tech.attempts || 0;
        const actionLines = (tech.actions || []).slice(0, 6).map(a => {
          const aTitle = (typeof a === 'string' ? a : a.title || 'Unnamed').substring(0, 35);
          const aResult = typeof a === 'object' ? (a.result || 'unknown') : 'unknown';
          const rColor = aResult === 'successful' ? '#4ade80' : aResult === 'detected' ? '#fbbf24' : aResult === 'blocked' ? '#f87171' : '#9ca3af';
          return `<tspan x="8" dy="14" fill="${rColor}" font-size="10">- ${this._escapeXml(aTitle)}</tspan>`;
        }).join('');

        const tooltipW = 260;
        const tooltipLineCount = Math.min((tech.actions || []).length, 6);
        const tooltipH = 65 + tooltipLineCount * 15;
        // Position tooltip to the right of the node, flip left if near edge
        const tooltipX = (cx + techBoxW / 2 + tooltipW + 10 < width) ? cx + techBoxW / 2 + 8 : cx - techBoxW / 2 - tooltipW - 8;
        const tooltipY = cy - 20;

        // Technique group with hover (node only, tooltip collected separately)
        elements.push(`<g class="kc-node" data-node="${nodeId}" style="cursor:pointer;">`);

        // Technique box
        elements.push(`<rect x="${cx - techBoxW / 2}" y="${cy - techBoxH / 2}" width="${techBoxW}" height="${techBoxH}" rx="5" fill="${boxColor}" opacity="0.85" class="kc-box"/>`);

        // Technique ID text
        elements.push(`<text x="${cx}" y="${cy + 4}" text-anchor="middle" fill="white" font-size="11" font-weight="bold">${techId}</text>`);

        // Technique name below box
        const truncName = techName.length > 18 ? techName.substring(0, 16) + '...' : techName;
        elements.push(`<text x="${cx}" y="${cy + techBoxH / 2 + 13}" text-anchor="middle" fill="${c.nameText}" font-size="8.5">${this._escapeXml(truncName)}</text>`);

        // Count badge (circle at top-right of box)
        if (attempts > 1) {
          elements.push(`<circle cx="${cx + techBoxW / 2 - 2}" cy="${cy - techBoxH / 2 + 2}" r="9" fill="${c.countBadgeBg}" stroke="${c.countBadgeStroke}" stroke-width="1.5"/>`);
          elements.push(`<text x="${cx + techBoxW / 2 - 2}" y="${cy - techBoxH / 2 + 6}" text-anchor="middle" fill="${c.countBadgeText}" font-size="9" font-weight="bold">${attempts}</text>`);
        }

        // Invisible hover area for tooltip trigger (part of kc-node group)
        elements.push(`<rect x="${cx - techBoxW / 2 - 5}" y="${cy - techBoxH / 2 - 10}" width="${techBoxW + 10}" height="${techBoxH + 25}" fill="transparent" class="kc-hover-area"/>`);

        elements.push(`</g>`);

        // Tooltip rendered separately (will be appended after all columns for correct z-order)
        tooltips.push(`<g class="kc-tip" data-for="${nodeId}" style="opacity:0;pointer-events:none;">
          <rect x="${tooltipX}" y="${tooltipY}" width="${tooltipW}" height="${tooltipH}" rx="8" fill="${c.tooltipBg}" stroke="#555" stroke-width="0.5"/>
          <text x="${tooltipX + 8}" y="${tooltipY + 18}" fill="white" font-size="12" font-weight="bold">${techId} - ${this._escapeXml(techName.substring(0, 28))}</text>
          <text x="${tooltipX + 8}" y="${tooltipY + 34}" fill="#94a3b8" font-size="10">Actions: ${attempts} | Success: ${pct}%</text>
          <text x="${tooltipX + 8}" y="${tooltipY + 48}" fill="#94a3b8" font-size="10">${actionLines}</text>
        </g>`);
      });
    });

    // Stats box (top-right)
    const totalActions = (techniques || []).reduce((s, t) => s + (t.attempts || 0), 0);
    const totalSuccess = (techniques || []).reduce((s, t) => s + (t.successful || Math.round((t.successRate || 0) * (t.attempts || 0))), 0);
    const overallRate = totalActions > 0 ? Math.round((totalSuccess / totalActions) * 100) : 0;
    const statsX = width - 195;
    const statsY = 5;
    elements.push(`<rect x="${statsX}" y="${statsY}" width="185" height="${statsH - 10}" rx="6" fill="${c.statsBg}" stroke="${c.statsStroke}" stroke-width="1"/>`);
    elements.push(`<text x="${statsX + 92}" y="${statsY + 20}" text-anchor="middle" fill="${c.statsText}" font-size="12" font-weight="bold">Attack Summary</text>`);
    const statsData = [
      { label: 'Total Actions:', value: totalActions },
      { label: 'Tactics Used:', value: populatedTactics.length + '/14' },
      { label: 'Techniques:', value: (techniques || []).length },
      { label: 'Success Rate:', value: overallRate + '%' }
    ];
    statsData.forEach((s, i) => {
      elements.push(`<text x="${statsX + 10}" y="${statsY + 40 + i * 18}" fill="${c.statsText}" font-size="11">${s.label}</text>`);
      elements.push(`<text x="${statsX + 175}" y="${statsY + 40 + i * 18}" text-anchor="end" fill="${c.statsText}" font-size="11" font-weight="bold">${s.value}</text>`);
    });

    // Legend
    const legendY = height - 22;
    const legendItems = [
      { color: '#28a745', label: 'Success >= 70%' },
      { color: '#ffc107', label: 'Success 30-70%' },
      { color: '#dc3545', label: 'Success < 30%' }
    ];
    legendItems.forEach((item, i) => {
      const lx = 15 + i * 160;
      elements.push(`<rect x="${lx}" y="${legendY}" width="14" height="10" fill="${item.color}" rx="2"/>`);
      elements.push(`<text x="${lx + 18}" y="${legendY + 9}" fill="${c.legendText}" font-size="10">${item.label}</text>`);
    });
    elements.push(`<text x="${width - 15}" y="${legendY + 9}" text-anchor="end" fill="${c.legendText}" font-size="10">Coverage: ${populatedTactics.length}/14 tactics</text>`);

    // CSS for hover highlight on boxes
    const style = `<style>
      .kc-node:hover .kc-box { opacity: 1 !important; stroke: #fff; stroke-width: 2; }
    </style>`;

    return `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
      ${style}
      ${elements.join('\n')}
      ${tooltips.join('\n')}
    </svg>`;
  },

  // Detect if data looks like chart data and return type
  detectChartType(data) {
    if (!data || typeof data !== 'object') return null;

    // Chart.js format: { labels, datasets }
    if (data.labels && data.datasets) return 'bar-chart-js';

    // Score/grade format
    if (data.score !== undefined && data.rating !== undefined) return 'scorecard';

    // Risk matrix format
    if (data.axes && data.risks) return 'risk-matrix';

    // Array of objects with value/label
    if (Array.isArray(data) && data.length > 0 && data[0].value !== undefined && data[0].label !== undefined) return 'bar-simple';

    return null;
  },

  // Convert Chart.js format data to SVG bar chart
  chartJsToSvg(chartJsData, options = {}) {
    if (!chartJsData.labels || !chartJsData.datasets) return '';

    const barColors = ['#2563eb', '#dc2626', '#22c55e', '#f59e0b', '#7c3aed'];
    const datasets = chartJsData.datasets;
    const labels = chartJsData.labels;

    if (datasets.length === 1) {
      // Single dataset - simple bar chart
      const data = labels.map((label, i) => ({
        label,
        value: datasets[0].data[i] || 0,
        color: barColors[0]
      }));
      return this.generateBarChart(data, { ...options, title: datasets[0].label || '' });
    }

    // Multiple datasets - grouped bars side by side
    const { width = 500, height = 300 } = options;
    const margin = { top: 50, right: 20, bottom: 60, left: 50 };
    const chartW = width - margin.left - margin.right;
    const chartH = height - margin.top - margin.bottom;
    const groupWidth = chartW / labels.length;
    const barWidth = Math.min(30, (groupWidth * 0.7) / datasets.length);
    const maxVal = Math.max(...datasets.flatMap(ds => ds.data), 1);

    const bars = [];
    labels.forEach((label, gi) => {
      const groupX = margin.left + gi * groupWidth + groupWidth * 0.15;

      datasets.forEach((ds, di) => {
        const val = ds.data[gi] || 0;
        const barH = (val / maxVal) * chartH;
        const x = groupX + di * (barWidth + 2);
        const y = margin.top + chartH - barH;
        const color = barColors[di % barColors.length];

        bars.push(`<rect x="${x}" y="${y}" width="${barWidth}" height="${barH}" fill="${color}" rx="2"/>
          <text x="${x + barWidth / 2}" y="${y - 3}" text-anchor="middle" fill="#374151" font-size="9">${val}</text>`);
      });

      bars.push(`<text x="${margin.left + gi * groupWidth + groupWidth / 2}" y="${margin.top + chartH + 18}" text-anchor="middle" fill="#6b7280" font-size="10">${label}</text>`);
    });

    // Legend
    const legend = datasets.map((ds, i) => {
      const lx = margin.left + i * 140;
      return `<rect x="${lx}" y="8" width="12" height="12" fill="${barColors[i % barColors.length]}" rx="2"/>
        <text x="${lx + 16}" y="18" fill="#374151" font-size="10">${ds.label}</text>`;
    }).join('\n');

    // Grid
    const grid = [0, 0.25, 0.5, 0.75, 1].map(pct => {
      const y = margin.top + chartH * (1 - pct);
      return `<line x1="${margin.left}" y1="${y}" x2="${margin.left + chartW}" y2="${y}" stroke="#f3f4f6" stroke-width="1"/>
        <text x="${margin.left - 5}" y="${y + 3}" text-anchor="end" fill="#9ca3af" font-size="9">${Math.round(maxVal * pct)}</text>`;
    }).join('\n');

    return `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
      ${legend}
      ${grid}
      <line x1="${margin.left}" y1="${margin.top + chartH}" x2="${margin.left + chartW}" y2="${margin.top + chartH}" stroke="#d1d5db"/>
      ${bars.join('\n')}
    </svg>`;
  }
};

export default reportChartService;
